import {
  HomeTheme,
  ThemeColors,
  ThemeConfig,
} from "../../../../service/BuyerConfiguration/BuyerAppDashboard/homeThemesService";

export const GREEN = "#00B14F";
export const GREEN_DARK = "#0a7a3c";

export const errMsg = (e: any, fallback: string): string => {
  const m = e?.response?.data?.message;
  if (Array.isArray(m)) return m.join(", ");
  return typeof m === "string" && m ? m : fallback;
};

/* ------------------------------- colours ------------------------------- */

/** Parses #rgb, #rrggbb, #rrggbbaa, rgb() and rgba() into 0..255 channels. */
export const parseColor = (c: string | null | undefined): { r: number; g: number; b: number; a: number } | null => {
  if (!c) return null;
  const s = c.trim();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(s);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h.split("").map((x) => x + x).join("");
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
      a: h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1,
    };
  }
  const rgb = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*([\d.]+)\s*)?\)$/i.exec(s);
  if (rgb) {
    return {
      r: Math.min(255, Number(rgb[1])),
      g: Math.min(255, Number(rgb[2])),
      b: Math.min(255, Number(rgb[3])),
      a: rgb[4] !== undefined ? Math.min(1, Number(rgb[4])) : 1,
    };
  }
  return null;
};

/** #rrggbb for the native colour input (alpha dropped). */
export const toHex6 = (c: string | null | undefined, fallback = "#000000"): string => {
  const p = parseColor(c);
  if (!p) return fallback;
  const h = (n: number) => Math.round(n).toString(16).padStart(2, "0");
  return `#${h(p.r)}${h(p.g)}${h(p.b)}`;
};

const luminance = (c: { r: number; g: number; b: number }) => {
  const ch = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * ch(c.r) + 0.7152 * ch(c.g) + 0.0722 * ch(c.b);
};

/** WCAG contrast ratio, or null when either colour cannot be read. */
export const contrastRatio = (a: string | null | undefined, b: string | null | undefined): number | null => {
  const pa = parseColor(a);
  const pb = parseColor(b);
  if (!pa || !pb) return null;
  const la = luminance(pa);
  const lb = luminance(pb);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

/** Text/background pairs that should stay readable (3:1 or better). */
export const contrastIssues = (c: ThemeColors): string[] => {
  const out: string[] = [];
  const check = (label: string, fg: string, bgs: Array<string | null | undefined>) => {
    const worst = bgs
      .map((bg) => contrastRatio(fg, bg))
      .filter((r): r is number => r !== null)
      .sort((x, y) => x - y)[0];
    if (worst !== undefined && worst < 3) out.push(`${label} (${worst.toFixed(1)}:1)`);
  };
  check("Text on the dash vs dash colours", c.onDash, [c.dashFrom, c.dashVia, c.dashTo]);
  check("Tab text vs tab colours", c.tabText, [c.tabFrom, c.tabTo]);
  check("Selected tab text vs selected tab fill", c.tabSelectedText, [c.tabSelectedFill]);
  check("Search text vs search bar", c.searchText, [c.searchBg]);
  return out;
};

/** Effective colours, with the night overrides applied when asked. */
export const effectiveColors = (cfg: ThemeConfig, night: boolean): ThemeColors =>
  night && cfg.night ? ({ ...cfg.colors, ...cfg.night } as ThemeColors) : cfg.colors;

/* -------------------------------- dates -------------------------------- */

/** ISO -> value for <input type="datetime-local"> (local time). */
export const isoToLocalInput = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** <input type="datetime-local"> value -> ISO (or null when empty). */
export const localInputToIso = (v: string): string | null => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

export const fmtDateTime = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
};

/* ------------------------------- status ------------------------------- */

export const inWindow = (t: Pick<HomeTheme, "starts_at" | "ends_at">, now = new Date()) =>
  (!t.starts_at || new Date(t.starts_at) <= now) && (!t.ends_at || new Date(t.ends_at) > now);

export const statusChip = (
  t: HomeTheme,
): { label: string; color: string; bg: string } => {
  switch (t.status) {
    case "live":
      return { label: "Live", color: "#fff", bg: GREEN };
    case "scheduled": {
      const range = `${fmtDateTime(t.starts_at)} – ${fmtDateTime(t.ends_at)}`;
      const ended = t.ends_at && new Date(t.ends_at) <= new Date();
      if (ended) return { label: `Schedule ended · ${range}`, color: "#6b6b6b", bg: "#EEEEEE" };
      return {
        label: inWindow(t) ? `Scheduled · on now until ${fmtDateTime(t.ends_at)}` : `Scheduled · ${range}`,
        color: "#0B4F8A",
        bg: "#E3F0FC",
      };
    }
    case "archived":
      return { label: "Archived", color: "#6b6b6b", bg: "#EEEEEE" };
    default:
      return { label: "Draft", color: "#8A5300", bg: "#FFF1D6" };
  }
};

export const deepEqual = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
